import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import tracking from '@g4/g4-analytics';

import RichContent from '@g4/rich-content';

import PlainLink from './PlainLink';
import PlainExternalLink from './PlainExternalLink';
import { interpolate } from '../utils/stringUtils';
import { hostAddress } from '../pageComponents/travelersPage/constants';

const isAbsoluteUrl = (url = '') => /^[a-z][a-z0-9+.-]*:/.test(url);

const OrderedList = styled.ol`
  list-style: decimal;
  li {
    list-style-type: decimal;
    list-style-position: inside;
  }
`;

const UnorderedList = styled.ul`
  list-style: disc;
  li {
    list-style-type: disc;
    list-style-position: inside;
  }
`;

const OpenInNewWindow = ({ href, children }) => (
  <PlainLink href={href} target="_blank" rel="noopener noreferrer">
    {children}
  </PlainLink>
);

OpenInNewWindow.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const getEventName = href => {
  const host = new URL(href).hostname;
  return host === hostAddress ? 'site_navigation_clicks' : 'click';
};

  const OpenExternalInNewWindow = ({ href, children }) => {
    return (
      <PlainExternalLink
        href={href}
        aria-label="Link will open in a new tab"
        onClick={() =>
          tracking.trackEvent({
            event: getEventName(href),
            link_text: children[0],
            link_url: href,
          })
        }
      >
        {children}
      </PlainExternalLink>
    );
  };

OpenExternalInNewWindow.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const a = ({ children, href }) => {
  if (isAbsoluteUrl(href)) {
    return (
      <PlainLink
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() =>
          tracking.trackEvent('site_navigation_clicks', href, children[0], null)
        }
      >
        {children}
      </PlainLink>
    );
  } else {
    return <PlainLink href={href}>{children}</PlainLink>;
  }
};

a.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const defaultCustomElements = {
  /**
   * Override rendering of markdown links to use a next link for onsite links
   * but open offsite links in a new window.
   */
  a: a,
  ol: OrderedList,
  ul: UnorderedList,
  OpenInNewWindow: OpenInNewWindow,
  OpenExternalInNewWindow: OpenExternalInNewWindow,
};

const Markdown = ({ customElements, data, source, forceInline }) => {
  const richContentSource = data ? interpolate(source, data) : source;
  return (
    <RichContent
      source={richContentSource}
      forceInline={forceInline}
      customElements={{ ...defaultCustomElements, ...customElements }}
    />
  );
};

Markdown.propTypes = {
  /**
   * A string of markdown suitable for consumption by the RichContent component
   * Interpolation markers are allowed and are set to `{{}}`
   */
  source: PropTypes.string.isRequired,
  /**
   * An object containing data to interpolate
   */
  data: PropTypes.object,
  /**
   * Passed to RichContent to force inline rendering
   */
  forceInline: PropTypes.bool,
  /**
   * Custom objects passed to the Markdown component that produce data to interpolate
   */
  customElements: PropTypes.object,
};

Markdown.defaultProps = {
  forceInline: false,
};

export default Markdown;
